/** @jsxImportSource @emotion/react */
import { AccordionDetails, AccordionSummary, Box, Link, Theme, Typography, useTheme } from '@mui/material';
import Accordion from 'components/Accordion';
import Heading from 'components/Heading';
import CommonPageLayout from 'components/layouts/CommonPageLayout';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { UseStyles } from 'types/styles';
import { asset } from 'utils/asset';
import { Helmet } from 'react-helmet';

const HelpPage: React.FC = () => {
  const theme = useTheme() as Theme;
  const classes = useStyles(theme);
  const { formatMessage } = useIntl();

  return (
    <CommonPageLayout>
      <Helmet>
        <title>{formatMessage({ id: 'pageTitle.help', defaultMessage: 'Help' })}</title>
      </Helmet>
      <Box p={8}>
        <Heading>
          <FormattedMessage id={'HelpPage.help'} defaultMessage={'Help'} />
        </Heading>
        <Box css={classes.items}>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>Introduction to National Planning</AccordionSummary>
            <AccordionDetails>
              <Box p={6} pl={8} pr={8} css={classes.content}>
                <Typography component="div" variant="body1">
                  The national planning process can be supported by the templates on this page, enabling countries to
                  develop integrated and coherent plans to support applications to the Pandemic Fund and other funding
                  sources. The process leverages existing tools and data which help to capture and assess capacities,
                  risks, outcomes from reviews, and prioritized actions.
                  <br />
                  <br />
                  The recommended four steps to develop plans are detailed below and were further described in the
                  webinar series which can be rewatched on{' '}
                  <Link
                    href="https://openwho.org/courses/webinar-pandemic-fund-proposals/"
                    target="_blank"
                    rel="noreferrer"
                  >
                    OpenWHO
                  </Link>
                  .
                  <br />
                  <br />
                  <img src={asset('/images/help/4steps.svg')} alt="" width="100%" style={{ maxWidth: 1100 }} />
                </Typography>
              </Box>
            </AccordionDetails>
          </Accordion>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>Tools and templates</AccordionSummary>
            <AccordionDetails>
              <Box p={6} pl={8} pr={8} css={classes.content}>
                <Typography component="div" variant="body1">
                  The following templates are available in the download section
                  <br />
                  <Box component="ul" pl={4}>
                    <li>
                      <strong>Defining team and wider process</strong>
                      <Box component="ul" mt={1} mb={4} pl={4}>
                        <li>Template for defining team and wider process</li>
                      </Box>
                    </li>
                    <li>
                      <strong>Identifying strategic priorities</strong>
                      <Box component="ul" mt={1} mb={4} pl={4}>
                        <li>
                          State Parties Self-Assessment Annual Reporting Tool (SPAR) and Joint External Evaluation (JEE)
                        </li>
                        <li>Strategic Tool for Assessing Risks (STAR)</li>
                        <li>Country profile</li>
                      </Box>
                    </li>
                    <li>
                      <strong>Developing prioritized action plan</strong>
                      <Box component="ul" mt={1} mb={4} pl={4}>
                        <li>NAPHS template</li>
                        <li>
                          <i>Integrated Plan Template</i>
                        </li>
                      </Box>
                    </li>
                    <li>
                      <strong>Costing of prioritized actions</strong>
                      <Box component="ul" mt={1} mb={4} pl={4}>
                        <li>
                          <i>Costing template</i>
                        </li>
                      </Box>
                    </li>
                    <li>
                      <strong>Mapping of existing resources</strong>
                      <Box component="ul" mt={1} mb={4} pl={4}>
                        <li>Resource Mapping (REMAP)</li>
                      </Box>
                    </li>
                    <li>
                      <strong>Developing funding proposals</strong>
                      <Box component="ul" mt={1} mb={4} pl={4}>
                        <li>Pandemic Fund application (from Pandemic Fund website)</li>
                      </Box>
                    </li>
                    <li>
                      <strong>Identifying roles and responsibilities and monitoring framework</strong>
                      <Box component="ul" mt={1} mb={4} pl={4}>
                        <li>
                          <i>Part of Pandemic fund application above</i>
                        </li>
                      </Box>
                    </li>
                  </Box>
                  These structured data templates enable users to enter, combine and integrate data from the broad range
                  of different tools available to inform and shape the development of national plans and funding
                  proposals. Templates can be downloaded and completed offline and reuploaded. By using these templates
                  we can standardize the way results and data are expressed across different tools, and broaden
                  inclusion and participation in the planning and proposal process to the greatest extent possible. For
                  each template in the download section, additional description and relevant links are available.
                </Typography>
              </Box>
            </AccordionDetails>
          </Accordion>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>Webinars and OpenWHO training course</AccordionSummary>
            <AccordionDetails>
              <Box p={6} pl={8} pr={8} css={classes.content}>
                <Typography component="div" variant="body1">
                  Further information on the Webinars supporting the development of pandemic fund proposals can be found
                  via OpenWHO through:{' '}
                  <Link
                    href="https://openwho.org/courses/webinar-pandemic-fund-proposals/"
                    target="_blank"
                    rel="noreferrer"
                  >
                    https://openwho.org/courses/webinar-pandemic-fund-proposals/
                  </Link>
                  .
                  <br />
                  <br />
                  Recordings of the five webinars are accessible below as they become available. Certificates are not
                  awarded for viewing these webinars.
                  <br />
                  <br />
                  <strong>27 March 2023</strong>
                  <br />
                  <strong>
                    <Link
                      href="https://openwho.org/courses/webinar-pandemic-fund-proposals/items/2h9mQrL4EmHBqLP7OoWkKK"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Day 1: Introduction and Overview
                    </Link>
                  </strong>
                  <br />
                  <br />
                  <strong>28 March 2023</strong>
                  <br />
                  <strong>
                    <Link
                      href="https://openwho.org/courses/webinar-pandemic-fund-proposals/items/3In3gBSHdlyrSpryaTWHGq"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Day 2: Capacity, threat, and vulnerability assessment
                    </Link>
                  </strong>
                  <br />
                  <br />
                  <strong>29 March 2023</strong>
                  <br />
                  <strong>
                    <Link
                      href="https://openwho.org/courses/b7492329-afcb-41a1-aeab-eac1311fa306/items/4P5PMPx9mxpN8bScjHHx5F"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Day 3: Prioritized and costed plans
                    </Link>
                  </strong>
                  <br />
                  <br />
                  <strong>30 March 2023</strong>
                  <br />
                  <strong>
                    <Link
                      href="https://openwho.org/courses/webinar-pandemic-fund-proposals/items/QbP5m36nqlNp5WqLunU5j"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Day 4: Resource mapping and mobilization
                    </Link>
                  </strong>
                  <br />
                  <br />
                  <strong>31 March 2023</strong>
                  <br />
                  <strong>
                    <Link
                      href="https://openwho.org/courses/webinar-pandemic-fund-proposals/items/1jRbvbaZGCxqlMqLPAfCbX"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Day 5: Implementation, monitoring and review
                    </Link>
                  </strong>
                  <br />
                  <br />
                  These webinars will:
                  <br />
                  <Box component="ul" pl={4}>
                    <li>
                      Describe the key elements and main tools for national planning to strengthen HEPR, and the
                      modalities of the first call for proposals
                    </li>
                    <li>Outline key considerations for capacity, threat, and vulnerability assessments</li>
                    <li>Clarify the approach to developing prioritized and costed plans</li>
                    <li>Highlight the main modalities to map and mobilize resources</li>
                    <li>Describe the approach to implementation, monitoring and review.</li>
                  </Box>
                </Typography>
              </Box>
            </AccordionDetails>
          </Accordion>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>Pandemic Fund Application Portal</AccordionSummary>
            <AccordionDetails>
              As informed on the{' '}
              <Link
                href="https://www.worldbank.org/en/programs/financial-intermediary-fund-for-pandemic-prevention-preparedness-and-response-ppr-fif/funding-opportunities"
                target="_blank"
                rel="noreferrer"
              >
                Pandemic Fund website
              </Link>
              , proposals to the Pandemic Fund must be submitted via the{' '}
              <Link
                href="https://worldbank.smapply.io/prog/the_pandemic_fund_first_call_for_proposal/"
                target="_blank"
                rel="noreferrer"
              >
                Pandemic Fund Application Portal
              </Link>{' '}
              by May 19, 2023 at 23:59 Eastern Time (ET). The optional templates and tools provided on this site are
              intended to support the development of proposals and broader national plans. Please note that completing
              and uploading these templates does not constitute an application to the Pandemic Fund.
            </AccordionDetails>
          </Accordion>
        </Box>
        <Box mt={6}>
          <Typography component="p" variant="body1">
            <FormattedMessage id={'HelpPage.needHelp'} defaultMessage={'Need help? Contact our support:'} />{' '}
            <Link href="mailto:partnersplatform-support@who.int">partnersplatform-support@who.int</Link>
          </Typography>
        </Box>
      </Box>
    </CommonPageLayout>
  );
};

const useStyles = (theme: Theme): UseStyles => ({
  items: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(4),
  },
  content: {
    img: {
      maxWidth: '100%',
      height: 'auto',
    },
  },
});

export default HelpPage;
